import PropTypes from 'prop-types';
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography,
  Box,
  CircularProgress,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { clearTransactAsData, selector } from 'reducers/transactAs';
import { selector as fspConfigPayALoanSelector } from 'selectors/fspConfigPayALoan';
import normalizeAssertionBaseUrl from 'util/normalizeAssertionBaseUrl';
import guestLoanUrlToEnrolledPalIfpUrl from 'util/guestLoanUrlToEnrolledPalIfpUrl';
import { buildPalIfpV2Url } from 'util/ifpGuestUrl';
import { mintEnrolledPayerPalJwt } from 'util/api';
import { getAuth } from 'util/index';
import { unmask as unmaskInternalAccount } from 'sdk/internalAccount';
import PaymentIframe from '../../PalGuestPayment/PaymentIframe';
import {
  buildIfpPalPaymentPopupFeatures,
  IFP_PAL_PAYMENT_POPUP_WINDOW_NAME,
} from '../../PalGuestPayment/popupConstants';
import { buildEnrolledPalContext, splitLoanAccountNumber } from './palContext';

const useStyle = makeStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  body: {
    paddingTop: theme.spacing(1),
    minHeight: theme.spacing(20),
  },
}));

function getPopupLoadingUrl() {
  return `${window.location.origin}/ifpPalPaymentLoading.html`;
}

export const resolveEnrolledPalContext = async (transactPayload, fspId) => {
  const ctx = buildEnrolledPalContext(transactPayload);
  if (ctx.loanNumber || !transactPayload.loanId || !fspId) {
    return ctx;
  }

  const unmaskedLoanNumber = await unmaskInternalAccount({
    id: transactPayload.loanId,
    fspId,
  });
  const parsed = splitLoanAccountNumber(unmaskedLoanNumber);

  if (!parsed.loanNumber) {
    throw new Error('Unable to resolve loan information for PAL payment.');
  }

  return {
    ...ctx,
    loanNumber: parsed.loanNumber,
    accountSuffix: ctx.accountSuffix || parsed.accountSuffix,
  };
};

const EnrolledPalAfterRegister = ({ payerName, onCancel, fspId }) => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const transactAsData = useSelector(selector) || {};
  const { registerData, data: transactPayload = {} } = transactAsData;
  const {
    loanId: transactLoanId,
    loanNumber: transactLoanNumber,
    accountSuffix: transactAccountSuffix,
    email: transactEmail,
  } = transactPayload;
  const effectiveFspId = useMemo(() => {
    const fromRegister = registerData?.payer?.fsp?.id;
    if (fromRegister != null && fromRegister !== '') {
      return fromRegister;
    }
    return fspId;
  }, [registerData, fspId]);

  const { id: adminUserId } = useSelector(getAuth);

  const fspConfigPayALoan = useSelector((state) =>
    fspConfigPayALoanSelector(state, effectiveFspId)
  );

  const [error, setError] = useState(null);
  const [minting, setMinting] = useState(true);
  const [jwt, setJwt] = useState(null);
  const [resolvedAssertionBaseUrl, setResolvedAssertionBaseUrl] =
    useState(null);
  const preOpenedPopupRef = useRef(null);
  const paymentPopupWindowRef = useRef(null);
  const startedRef = useRef(false);

  const assertionBaseUrl = useMemo(() => {
    const raw = fspConfigPayALoan?.guestUrl;
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) {
      return normalizeAssertionBaseUrl(guestLoanUrlToEnrolledPalIfpUrl(raw));
    }
    return normalizeAssertionBaseUrl(buildPalIfpV2Url(raw));
  }, [fspConfigPayALoan]);

  const exitFlow = useCallback(() => {
    if (
      preOpenedPopupRef.current &&
      typeof preOpenedPopupRef.current.close === 'function'
    ) {
      try {
        preOpenedPopupRef.current.close();
      } catch (_e) {
        // ignore
      }
      preOpenedPopupRef.current = null;
    }
    paymentPopupWindowRef.current = null;
    dispatch(clearTransactAsData());
    onCancel();
  }, [dispatch, onCancel]);

  const handlePopupReady = useCallback((popupWindow) => {
    paymentPopupWindowRef.current = popupWindow;
  }, []);

  const handlePopupClosed = useCallback(() => {
    exitFlow();
  }, [exitFlow]);

  useEffect(() => {
    if (startedRef.current) {
      return undefined;
    }
    if (!registerData?.payer?.id || !registerData?.user?.id) {
      setError('Missing payer registration data for PAL payment.');
      setMinting(false);
      return undefined;
    }
    if (!assertionBaseUrl) {
      setError('PAL IFP URL is not configured for this FSP.');
      setMinting(false);
      return undefined;
    }

    startedRef.current = true;

    const run = async () => {
      const popup = window.open(
        getPopupLoadingUrl(),
        IFP_PAL_PAYMENT_POPUP_WINDOW_NAME,
        buildIfpPalPaymentPopupFeatures()
      );

      if (!popup || popup.closed || popup.closed === undefined) {
        setError(
          'Popup window was blocked. Please allow popups for this site and try again.'
        );
        setMinting(false);
        startedRef.current = false;
        return;
      }

      preOpenedPopupRef.current = popup;
      try {
        popup.focus();
      } catch (_e) {
        // ignore
      }

      try {
        const enrolledPalContext = await resolveEnrolledPalContext(
          {
            loanId: transactLoanId,
            loanNumber: transactLoanNumber,
            accountSuffix: transactAccountSuffix,
            email: transactEmail,
          },
          effectiveFspId
        );
        const result = await mintEnrolledPayerPalJwt(effectiveFspId, {
          payerId: registerData.payer.id,
          userId: registerData.user.id,
          assertionBaseUrl,
          adminUserId,
          ...enrolledPalContext,
        });
        setJwt(result.jwt);
        setResolvedAssertionBaseUrl(
          result.assertionBaseUrl || assertionBaseUrl
        );
      } catch (err) {
        const message =
          (err && err.data && err.data.message) ||
          (err && err.message) ||
          'Unable to start PAL payment.';
        setError(message);
        try {
          popup.close();
        } catch (_e2) {
          // ignore
        }
        preOpenedPopupRef.current = null;
      } finally {
        setMinting(false);
      }
    };

    run();
    return undefined;
  }, [
    adminUserId,
    assertionBaseUrl,
    effectiveFspId,
    registerData,
    transactAccountSuffix,
    transactEmail,
    transactLoanId,
    transactLoanNumber,
  ]);

  return (
    <>
      <DialogTitle>
        Transact As {payerName} — PAL payment
        <IconButton
          aria-label="Close Transact As Dialog"
          className={classes.closeButton}
          color="inherit"
          onClick={exitFlow}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Box className={classes.body}>
        {minting && !error && (
          <Box textAlign="center" py={3}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Preparing secure payment session…
            </Typography>
          </Box>
        )}
        {error && (
          <Box py={2}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
            <Box mt={2}>
              <Button color="primary" onClick={exitFlow} variant="contained">
                Close
              </Button>
            </Box>
          </Box>
        )}
        {!minting && jwt && resolvedAssertionBaseUrl && (
          <Box py={1}>
            <Typography variant="body2" color="textSecondary" paragraph>
              Complete your payment in the popup window. When you are done, you
              may close the popup or use Close here to return to payer detail.
            </Typography>
            <PaymentIframe
              assertionBaseUrl={resolvedAssertionBaseUrl}
              fspId={effectiveFspId}
              ifpAssertionVariant="enrolled"
              jwt={jwt}
              onPopupClosed={handlePopupClosed}
              onPopupReady={handlePopupReady}
              preOpenedPopup={preOpenedPopupRef.current}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

EnrolledPalAfterRegister.propTypes = {
  payerName: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  fspId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default EnrolledPalAfterRegister;
